import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import {
    FalconNavigatorHeader,
    ICustomerPortalService,
    Policy,
    productHelper,
    resourcesHelper,
    Types,
    POLICY_STATUS_MTC,
    SESSION_STORAGE_KEY_CLAIM_POLICY
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {resolveFuelType, resolveTransmission} from "../../../../../utils/resolveLookupValues";
import * as moment from "moment";
import {FalconAccordion} from "../../../../../components/FalconAccordion";
import { getAddress, scrollToTop, setSessionStorage } from "../../../../../CommonFunctions";
import {getInteractionVehicleFuelType} from "@falcon/plugin-falcon-journey";
import {AllianzOnlineBadge} from "../../../../../components/AllianzOnlineBadge";

interface IState {
    rentersPolicies: Policy[];
    motorPolicies: Policy[];
    policiesLoaded: boolean;
    selectedPolicy?: Policy;
}

@authorize
export class SelectPolicyPagePlain extends React.Component<IWithRouterProps, IState> {

    private visibleItems: number = 2;
    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IWithRouterProps) {
        super(props);

        this.state = {
            rentersPolicies: [],
            motorPolicies: [],
            policiesLoaded: false,
        };
    }

    public componentDidMount(): void {
        scrollToTop();
        this.pluginTemplates.setPageTitle("Select coverage");

        this.customerPortalService.getPolicies().subscribe((policies: Policy[]) => {
            const rentersPolicies: Policy[] = policies.filter(policy => productHelper.isRenters(policy.interaction))
                .sort((a, b) => moment(b.startDate).diff(a.startDate));
            const motorPolicies: Policy[] = policies.filter(policy => productHelper.isMotor(policy.interaction))
                .sort((a, b) => moment(b.startDate).diff(a.startDate));
            this.setState({
                rentersPolicies,
                motorPolicies,
                policiesLoaded: true
            });
        });
    }

    public render() {
        return (
            <React.Fragment>

                <FalconHeader/>
                <div className="page-contents">
                    <AllianzOnlineBadge/>
                    <FalconNavigatorHeader
                        back="/dashboard/monthlySubscription"
                        title="On which of the below covers do you want to make a claim?"
                    />

                    {this.state.rentersPolicies.length > 0 &&
                    <div className="panel">
                        <div className="text-center">
                            <img className="" src="/img/Flow-renters.svg" alt="Renters coverages"
                                 aria-label="Renters coverages" tabIndex={0}/>
                            <div className="font-weight-bold my-4">Renters cover(s)</div>
                        </div>
                        {this.renderPolicyAccordion(this.state.rentersPolicies)}
                    </div>
                    }

                    {this.state.motorPolicies.length > 0 &&
                    <div className="panel">
                        <div className="text-center">
                            <img className="main-icon" src={`/${resourcesHelper.THEME}/img/wolf-car-icon.png`} alt="Motor coverages"
                                 aria-label="Motor coverages" tabIndex={0}/>
                            <div className="font-weight-bold my-4">Car cover(s)</div>
                        </div>
                        {this.renderPolicyAccordion(this.state.motorPolicies)}
                    </div>
                    }

                    <div>
                        <button type="button" className="btn btn-primary btn-next plugin"
                                id="submitMakeRentersClaim"
                                disabled={!this.isFormValid()}
                                onClick={this.handleRedirect}>Next</button>
                    </div>

                    {this.pluginTemplates.renderBackLink("Back", false, "/dashboard/monthlySubscription")}
                </div>
                <Overlay show={!this.state.policiesLoaded}/>
                <FalconFooter/>


            </React.Fragment>
        );
    }

    private isFormValid = (): boolean => {
        return this.state.selectedPolicy !== undefined;
    }

    private handleRedirect = (event: any): void => {
        setSessionStorage(SESSION_STORAGE_KEY_CLAIM_POLICY, this.state.selectedPolicy);

        if (event.target.disabled){
            event.preventDefault();
        } else if (productHelper.isMotor(this.state.selectedPolicy!.interaction)) {
            this.props.navigate("/dashboard/windscreen");
        } else if (productHelper.isRenters(this.state.selectedPolicy!.interaction)){
            this.props.navigate("/dashboard/rentersClaims/new");
        }
    };

    private handleOnChangeEvent = (event: any, policy: Policy): void => {
        this.setState({selectedPolicy:policy})
    };

    private renderPolicyAccordion = (policyList: Policy[]): React.ReactNode => {
        return (
            <React.Fragment>
                {this.renderPolicyItemsList(policyList,true)}
                {policyList.length > this.visibleItems &&
                <FalconAccordion toggleHidden={<div className="toggle">+ Show more</div>}
                                 toggleShown={<div className="toggle">- Show less</div>}>
                    {this.renderPolicyItemsList(policyList,false)}
                </FalconAccordion>}
            </React.Fragment>
        )
    };

    private renderActiveBubble = (policy: Policy): React.ReactNode => {
        return (
            <React.Fragment>
                {moment().isBetween(moment(policy.startDate), moment(policy.endDate), null, "(]") &&
                <div className="mt-2"><span
                    className="accent-text font-weight-semi-bold">Active</span></div>}
            </React.Fragment>
        )
    };

    private renderPolicyDates = (policy: Policy): React.ReactNode | null => {
        const startDate: moment.Moment = moment.utc(policy.startDate, moment.ISO_8601);
        const endDate: moment.Moment = moment.utc(policy.endDate, moment.ISO_8601);
        const format = "D MMMM YYYY";
        if (policy.status === POLICY_STATUS_MTC && endDate.isBefore(moment())){
            return (
                <div>({startDate.format(format)} - {endDate.format(format)})</div>
            )
        }
        return null;
    };

    private renderPolicyItemsList = (policyList: Policy[],renderTopOnly: boolean): React.ReactNode[] =>{
        return (
            policyList.map((policy: Policy, index) => (
                    <>
                        {((index < this.visibleItems && renderTopOnly) || (index >= this.visibleItems && !renderTopOnly)) &&
                        <div className="custom-control margin-bottom-20-padding-left-0 text-nowrap">
                            {index !== 0 && <div className="dashed-10 margin-bottom-20-padding-left-0"/>}
                            <input id={policy.policyNumber+"radio"}
                                   className="custom-radiobutton select-coverage-radio"
                                   type="radio"
                                   name="policySelect"
                                   value={policy.policyNumber}
                                   checked={this.state.selectedPolicy?.policyNumber === policy.policyNumber}
                                   onChange={(e: any) => this.handleOnChangeEvent(e, policy)}/>
                            <label className="checkbox-label select-coverage-label"
                                   htmlFor={policy.policyNumber+"radio"}>
                                {productHelper.isMotor(policy.interaction) &&
                                <React.Fragment>
                                    <div
                                        className="font-weight-semi-bold">{`${policy.interaction.vehicles[0].make}, ${policy.interaction.vehicles[0].model} (${policy.interaction.vehicles[0].registrationNumber})`}</div>
                                    <div
                                        className="">{`${policy.interaction.vehicles[0].year}, ${resolveTransmission(policy.interaction.vehicles[0].transmission)}, ${ resolveFuelType(getInteractionVehicleFuelType(policy.interaction.vehicles[0].fuelType, policy.interaction.vehicles[0].fuelTypeFull)) }`}</div>
                                    {this.renderActiveBubble(policy)}
                                </React.Fragment>}
                                {productHelper.isRenters(policy.interaction) &&
                                <React.Fragment>
                                    <div className="">{getAddress(policy.policyHolder.address)} {policy.policyHolder.address.postalCode}</div>
                                    {this.renderActiveBubble(policy)}
                                </React.Fragment>}
                                {this.renderPolicyDates(policy)}
                            </label>
                        </div>
                        }
                    </>

                ))
        );
    }
}

export const SelectPolicyPage = withRouter(SelectPolicyPagePlain)
